import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable } from 'rxjs';

export interface Booking {
  id?: string;
  firstName?: string;
  lastName?: string;
  customerName?: string; // Backwards compatibility
  companyName?: string;
  phone: string;
  email: string;
  address: string;
  street?: string;
  postalCode?: string;
  city?: string;
  billingStreet?: string;
  billingPostalCode?: string;
  billingCity?: string;
  startDate: Date;
  endDate: Date;
  distance: number;
  durationSeconds?: number;
  durationDays: number;
  price?: number;
  helperDiscount?: number;
  status?: string;
  magicToken?: string;
  notes?: string;
  includes360Branding?: boolean;
  brandingNotes?: string;
  customerProvidesHelper?: boolean;
  lineItems?: OrderLineItem[];
}

export interface OrderLineItem {
  description: string;
  quantity: number;
  unitPrice: number;
  totalPrice: number;
}

@Injectable({
  providedIn: 'root'
})
export class BookingService {
  private apiUrl = '/api/bookings';

  constructor(private http: HttpClient) {}

  getAvailableDates(startDate: Date, endDate: Date): Observable<any> {
    return this.http.get<any>(`${this.apiUrl}/available`, {
      params: {
        startDate: startDate.toISOString(),
        endDate: endDate.toISOString()
      }
    });
  }

  calculatePrice(distance: number, durationDays: number, durationSeconds?: number, durationHours?: number, customerProvidesHelper?: boolean, includes360Branding?: boolean, startDate?: Date, endDate?: Date, requestedLocation?: string, requestedCity?: string): Observable<any> {
    return this.http.post<any>(`${this.apiUrl}/calculate-price`, {
      distance,
      durationDays,
      durationSeconds: durationSeconds || 0,
      durationHours: durationHours || 0,
      startDate: startDate ? startDate.toISOString() : null,
      endDate: endDate ? endDate.toISOString() : null,
      requestedLocation: requestedLocation || null,
      requestedCity: requestedCity || null,
      customerProvidesHelper: customerProvidesHelper || false,
      includes360Branding: includes360Branding || false
    });
  }

  createBooking(booking: Booking): Observable<any> {
    return this.http.post<any>(this.apiUrl, booking);
  }

  getBooking(bookingId: string, token: string): Observable<any> {
    return this.http.get<any>(`${this.apiUrl}/${bookingId}`, {
      params: { token }
    });
  }

  updateBooking(bookingId: string, token: string, booking: Partial<Booking>): Observable<any> {
    return this.http.put<any>(`${this.apiUrl}/${bookingId}`, booking, {
      params: { token }
    });
  }

  cancelBooking(bookingId: string, token: string): Observable<any> {
    return this.http.delete<any>(`${this.apiUrl}/${bookingId}`, {
      params: { token }
    });
  }

  getPricingConfiguration(): Observable<any> {
    return this.http.get<any>('/api/pricing/configuration');
  }

  calculateHelperDiscount(distance: number, durationDays: number, includes360Branding: boolean, startDate?: Date, endDate?: Date, requestedLocation?: string, requestedCity?: string): Observable<number> {
    // Berechne Preis ohne Helfer
    const withoutHelper$ = this.calculatePrice(distance, durationDays, 0, 0, false, includes360Branding, startDate, endDate, requestedLocation, requestedCity);
    // Berechne Preis mit Helfer
    const withHelper$ = this.calculatePrice(distance, durationDays, 0, 0, true, includes360Branding, startDate, endDate, requestedLocation, requestedCity);
    
    // Kombiniere beide Requests und berechne die Differenz
    return new Observable(observer => {
      Promise.all([
        withoutHelper$.toPromise(),
        withHelper$.toPromise()
      ]).then(([withoutHelper, withHelper]) => {
        const discount = withHelper.totalPrice - withoutHelper.totalPrice;
        observer.next(discount);
        observer.complete();
      }).catch(error => {
        observer.error(error);
      });
    });
  }
}
